import { useMemo } from 'react';

import SidebarCard from '../ui/SidebarCard';
import { useExplorationActions, useExplorationState } from '../../context/ExplorationContext';
import { useExplorationTooltipHandlers } from '../../hooks/useExplorationTooltipHandlers';
import { currentDrillAncestryChips } from '../../lib/bubble/ancestryChips';
import { removeExplorationFilter } from '../../lib/filters/removeExplorationFilter';
import { bucketize } from '../../lib/sidebar/bucketize';
import {
  buildVolumeBreakdownData,
  type BreakdownSegment,
} from '../../lib/sidebar/buildBreakdownData';
import { breakdownBarColor } from '../../lib/sidebar/signalColors';
import {
  sidebarBreakdownPalette,
  type SidebarBreakdownVariant,
} from '../../lib/sidebar/sidebarPalettes';
import { statusBreakdownCardHtml } from '../../lib/tooltipHtml';

import StatusRow from './StatusRow';

import type { ExplorationFilter, ExplorationVolume } from '../../types/exploration';

interface SidebarBreakdownCardProps {
  volumes: ExplorationVolume[];
  variant: SidebarBreakdownVariant;
}

export default function SidebarBreakdownCard({ volumes, variant }: SidebarBreakdownCardProps) {
  const state = useExplorationState();
  const { setHighlight, addFilter, setFilters } = useExplorationActions();
  const { showTooltip, hideTooltip } = useExplorationTooltipHandlers();
  const palette = sidebarBreakdownPalette(variant);
  const signalKey = palette.order[0];
  const colorAllKeys = variant === 'activation';

  const data = useMemo(
    () =>
      buildVolumeBreakdownData(volumes, palette.field, palette.order, palette.label, palette.color),
    [volumes, palette]
  );

  const ancestry = useMemo(
    () =>
      currentDrillAncestryChips({
        location: state.drillPath,
        secondary: state.secondary,
        groupByAccount: state.groupByAccount,
      }),
    [state.drillPath, state.secondary, state.groupByAccount]
  );

  const bucketsWithVolumes = useMemo(
    () => bucketize(volumes, palette.field, palette.order, { collectItems: true }),
    [volumes, palette.field, palette.order]
  );

  const activeValues = useMemo(
    () =>
      new Set(
        state.filters
          .filter(
            (f): f is Extract<ExplorationFilter, { type: 'activationStatus' | 'supportStatus' }> =>
              f.type === palette.field
          )
          .map((f) => f.value)
      ),
    [state.filters, palette.field]
  );

  const toggleStatusFilter = (key: string) => {
    const filter: ExplorationFilter = { type: palette.field, value: key };
    if (activeValues.has(key)) {
      setFilters(removeExplorationFilter(state.filters, filter));
    } else {
      addFilter(filter);
    }
  };

  const showSegmentTooltip = (segment: BreakdownSegment, x: number, y: number) => {
    setHighlight({ kind: 'status', key: segment.key });
    const html = statusBreakdownCardHtml(
      {
        type: variant === 'support' ? 'reason' : 'status',
        name: segment.label,
        key: segment.key,
        value: segment.size,
        count: segment.count,
        _volumes: bucketsWithVolumes.get(segment.key)?.items ?? [],
      },
      // accountTotalSize is unused by statusBreakdownCardHtml today (only ctx.ancestry is read).
      { accountTotalSize: 0, ancestry }
    );
    showTooltip(html, x, y, 'status');
  };

  const hideSegmentTooltip = () => {
    setHighlight(null);
    hideTooltip();
  };

  return (
    <SidebarCard variant="interactive" className="pt-3">
      {data.segments.map((segment) => (
        <StatusRow
          key={segment.key}
          label={segment.label}
          size={segment.size}
          count={segment.count}
          pct={segment.flexPct}
          barColor={breakdownBarColor(segment.key, {
            signalKey,
            colorForKey: palette.color,
            colorAllKeys,
          })}
          selected={activeValues.has(segment.key)}
          onClick={() => toggleStatusFilter(segment.key)}
          onMouseEnter={(e) => showSegmentTooltip(segment, e.clientX, e.clientY)}
          onMouseLeave={hideSegmentTooltip}
          onFocus={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            showSegmentTooltip(segment, rect.right, rect.top);
          }}
          onBlur={hideSegmentTooltip}
        />
      ))}
    </SidebarCard>
  );
}
